import cx from 'classnames';
import type { ReactNode } from 'react';
import {
  EditRelevanceLink,
  editRelevanceLinkWrapperClass
} from './EditRelevanceLink';
import { BasicVenueCard, FeaturedVenueCard } from 'common/components/VenueCard';
import { SearchActionCreators } from 'search/actions/SearchActionCreators';
import type { VenueResult, HighlightCapacityType } from 'search/types';

type CommonProps = {
  venue: VenueResult;
  openInNewTab: boolean;
  isActive?: boolean;
  onClickVenueLink?: () => void;
  highlightCapacity: HighlightCapacityType;
};

/*
 * NOTE: Disable needed because this field mirrors a snake_case server
 * payload. The rule is configured `{ properties: 'never' }`, but the base
 * ESLint `camelcase` rule is not TS-aware: it reports `TSPropertySignature`
 * nodes as plain identifiers, so that exemption never applies to a type
 * declaration. The Flow original produced no warnings for the same field.
 */
/* eslint-disable camelcase */
type BasicVariantProps = CommonProps & {
  variant: 'BASIC';
  features?: { edit_relevance?: boolean };
};
/* eslint-enable camelcase */

type FeaturedVariantProps = CommonProps & {
  variant: 'FEATURED';
  priority: 'REGULAR' | 'HIGH';
};

export type Props = BasicVariantProps | FeaturedVariantProps;

function handleFocus(venueId: number) {
  SearchActionCreators.focusSearchResult(venueId);
}

export function VenuePreview(props: Props): ReactNode {
  const {
    venue,
    openInNewTab = true,
    isActive,
    onClickVenueLink,
    highlightCapacity = 'SITTING'
  } = props;

  let warning: 'unpublished' | 'restricted' | undefined;
  if (!venue.published) {
    warning = 'unpublished';
  } else if (venue.restricted) {
    warning = 'restricted';
  }

  const commonVenueData = {
    capacity_sitting: venue.capacity_sitting,
    capacity_standing: venue.capacity_standing,
    city: venue.city,
    has_availability_data: venue.has_availability_data,
    id: venue.id,
    locale: venue.locale,
    reviews_average: venue.reviews_average,
    reviews_count: venue.reviews_count,
    title: venue.title,
    url: venue.url,
    preview_images: venue.preview_images,
    rent_per_day: venue.rent_per_day,
    rent_per_person: venue.rent_per_person,
    rent_per_hour: venue.rent_per_hour,
    sales_guarantee: venue.sales_guarantee,
    warning
  };

  const focusHandlers = {
    onFocus: () => handleFocus(venue.id),
    onMouseEnter: () => handleFocus(venue.id)
  };

  if (props.variant === 'FEATURED') {
    const featuredProps: FeaturedVariantProps = props;
    return (
      <div {...focusHandlers}>
        <FeaturedVenueCard
          venue={commonVenueData}
          priority={featuredProps.priority}
          onClickVenueLink={onClickVenueLink}
          highlightCapacity={highlightCapacity}
          openInNewTab={openInNewTab}
        />
      </div>
    );
  }

  const previewBoxClass = cx({
    'b-search-result': true,
    'is-active': isActive
  });

  return (
    <li {...focusHandlers} className={editRelevanceLinkWrapperClass}>
      <div className={previewBoxClass} data-testid="regular-search-result">
        <BasicVenueCard
          venue={commonVenueData}
          openInNewTab={openInNewTab}
          onClickVenueLink={onClickVenueLink}
          highlightCapacity={highlightCapacity}
        />
      </div>
      {props.features?.edit_relevance && (
        <EditRelevanceLink venueId={venue.id} />
      )}
    </li>
  );
}
